import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Spinner, Switch, Text } from '@chakra-ui/react';
import xhr from 'xhr';
import OrdinalsPicker from 'pages/administrador/filtro/reporte-pagos/components/LinkPayments/OrdinalsPicker';

/**
 * Sección "¿Cuántas materias aporta este pago?" del modal de nuevo pago,
 * para programas is_semester_based. Reemplaza la grilla de meses.
 *
 * Reutiliza el OrdinalsPicker del modal de vinculación y el endpoint de
 * candidatos para saber el total de materias del programa y qué cantidades
 * ya están tomadas por pagos anteriores de la matrícula.
 *
 * Emite por onStateChange: { ordinals:number[], skipOrdinals:boolean, graduationFeePaid:boolean|undefined }
 */
export default function SubjectsCantidadesPicker({
  enrollmentType, // 'grade' | 'technical_career'
  enrollment, // objeto REST de la matrícula (id, semester, graduation_fee_paid, ...)
  hasGraduationFee,
  onStateChange,
  error,
}) {
  const enrollmentId = enrollment?.id;
  const [loading, setLoading] = useState(true);
  const [subjectsTotal, setSubjectsTotal] = useState(0);
  const [blockedInDb, setBlockedInDb] = useState(new Map());
  const [selected, setSelected] = useState([]);
  const [skipOrdinals, setSkipOrdinals] = useState(false);
  const [gradFee, setGradFee] = useState(false);

  useEffect(() => {
    setGradFee(enrollment?.graduation_fee_paid === true || enrollment?.attributes?.graduation_fee_paid === true);
  }, [enrollmentId]); // eslint-disable-line

  useEffect(() => {
    if (!enrollmentId || !enrollmentType) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ enrollment_type: enrollmentType, enrollment_id: String(enrollmentId) });
        const res = await xhr.get(`/payments/manual-link/candidates?${params.toString()}`);
        const payload = res.data?.data || {};
        if (cancelled) return;
        setSubjectsTotal(payload.meta?.subjectCount || 0);
        const claimed = (payload.claimedOrdinalsByEnrollment || {})[enrollmentId] || [];
        setBlockedInDb(new Map(claimed.map((c) => [Number(c.ordinal), c.paymentId])));
      } catch (err) {
        console.error('SubjectsCantidadesPicker load:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enrollmentId, enrollmentType]);

  useEffect(() => {
    if (!onStateChange) return;
    onStateChange({
      ordinals: skipOrdinals ? [] : selected,
      skipOrdinals,
      graduationFeePaid: hasGraduationFee ? gradFee : undefined,
    });
  }, [selected, skipOrdinals, gradFee, hasGraduationFee, onStateChange]);

  const emptyPeers = useMemo(() => new Map(), []);

  const toggle = (n) => {
    if (blockedInDb.has(n)) return;
    setSelected((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  };

  return (
    <Box marginTop="10px" marginBottom="20px" border="1px solid #e2e8f0" borderTopRadius="5px">
      {/* Mismo encabezado que "¿Qué otros pagos cancela el estudiante?" */}
      <Box
        color={error ? 'red.700' : 'purple.700'}
        fontWeight="500"
        paddingY="8px"
        paddingX="15px"
        backgroundColor={error ? 'red.100' : 'purple.200'}
        borderTopRadius="5px"
      >
        ¿Qué materias paga el estudiante? *
      </Box>
      <Box padding="15px">
        <Flex align="center" gap={2} mb={4}>
          <Switch
            isChecked={skipOrdinals}
            onChange={() => setSkipOrdinals((v) => !v)}
            colorScheme="purple"
            size="sm"
          />
          <Box flex="1">
            <Text fontSize="12px" fontWeight="600" color={skipOrdinals ? 'purple.700' : 'gray.700'}>
              Este pago no cubre materias
            </Text>
            <Text fontSize="11px" color="gray.500">
              Se vinculará a la matrícula pero no aportará cantidades al reporte (ej. Servicio Social, Derecho de Grado).
            </Text>
          </Box>
        </Flex>

        {loading ? (
          <Flex justify="center" py={4}><Spinner size="sm" /></Flex>
        ) : (
          <Box opacity={skipOrdinals ? 0.5 : 1} pointerEvents={skipOrdinals ? 'none' : 'auto'}>
            <OrdinalsPicker
              subjectsTotal={subjectsTotal}
              selected={selected}
              reservedByPeers={emptyPeers}
              blockedInDb={blockedInDb}
              onToggle={toggle}
              disabled={skipOrdinals}
            />
          </Box>
        )}

        {hasGraduationFee && (
          <Flex alignItems="center" gap="10px" mt={4}>
            <Text fontSize="13px" color="gray.700">¿Está al día en derechos de grado?</Text>
            <Switch isChecked={gradFee} onChange={() => setGradFee((v) => !v)} />
          </Flex>
        )}
      </Box>
    </Box>
  );
}
